// BulkEditStage2Rows.tsx — CTSK-517. The five Stage 2 palette rows for
// POST /documents/bulk-edit: Tags, Container, Custom properties, Frontmatter,
// Work-item links. Same No change / Set / Clear picker as the label rows;
// every row starts at "No change", so opening the palette and changing nothing
// builds an empty mutation set (Apply stays disabled).
//
// Everything that turns text into a request lives in pure functions
// (buildStage2Mutations / stage2Errors / describeStage2) so it is unit-testable
// without rendering. Values are entered as plain text:
//   * lists  — comma or newline separated (tag names, work-item keys, CP names)
//   * maps   — one `key=value` per line (custom properties, frontmatter)
// Custom property values: `true`/`false` and plain numbers are typed; wrap a
// value in double quotes ("007") to force text. Frontmatter values are read as
// JSON when they parse (["a","b"], 3, true) and as text otherwise.

import { useId } from 'react';
import { LINK_CONTEXTS } from '@captain-tasko/shared';
import type { DocumentBulkEdit } from '@captain-tasko/shared';

export type RowMode = 'NO_CHANGE' | 'REPLACE' | 'CLEAR';

export interface Stage2State {
  tags: { mode: RowMode; tagMode: 'replace' | 'add' | 'remove'; names: string };
  container: { mode: RowMode; name: string };
  customProperties: { mode: RowMode; values: string; replaceAll: boolean; clearNames: string };
  frontmatter: { mode: RowMode; replaceWhole: boolean; values: string; clearKeys: string };
  workItemLinks: { mode: RowMode; context: (typeof LINK_CONTEXTS)[number]; items: string };
}

export const EMPTY_STAGE2: Stage2State = {
  tags: { mode: 'NO_CHANGE', tagMode: 'replace', names: '' },
  container: { mode: 'NO_CHANGE', name: '' },
  customProperties: { mode: 'NO_CHANGE', values: '', replaceAll: false, clearNames: '' },
  frontmatter: { mode: 'NO_CHANGE', replaceWhole: false, values: '', clearKeys: '' },
  workItemLinks: { mode: 'NO_CHANGE', context: 'reference', items: '' },
};

export type Stage2Mutations = Partial<
  Pick<DocumentBulkEdit['mutations'], 'tags' | 'container' | 'customProperties' | 'frontmatter' | 'workItemLinks'>
>;

export const splitList = (text: string): string[] =>
  [...new Set(text.split(/[\n,]/).map((s) => s.trim()).filter(Boolean))];

type Parsed<T> = { ok: true; value: T } | { ok: false; message: string };

/** `key=value` lines -> map. `scalar` decides how a value string is typed. */
export function parseKeyValues(text: string, scalar: (raw: string) => unknown): Parsed<Record<string, unknown>> {
  const out: Record<string, unknown> = {};
  for (const line of text.split('\n')) {
    if (!line.trim()) continue;
    const eq = line.indexOf('=');
    const key = (eq === -1 ? line : line.slice(0, eq)).trim();
    if (eq === -1 || !key) return { ok: false, message: `Use key=value on each line (got "${line.trim()}").` };
    out[key] = scalar(line.slice(eq + 1).trim());
  }
  return { ok: true, value: out };
}

export function cpScalar(raw: string): string | number | boolean {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  if (/^-?\d+(\.\d+)?$/.test(raw)) return Number(raw);
  if (raw.length >= 2 && raw.startsWith('"') && raw.endsWith('"')) return raw.slice(1, -1);
  return raw;
}

export function fmScalar(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

/** One message per row that is on Set/Clear but not yet sendable. Empty = fine. */
export function stage2Errors(s: Stage2State): Partial<Record<keyof Stage2State, string>> {
  const e: Partial<Record<keyof Stage2State, string>> = {};
  if (s.tags.mode === 'REPLACE' && splitList(s.tags.names).length === 0) {
    e.tags = 'Enter at least one tag name, or choose Clear.';
  }
  if (s.container.mode === 'REPLACE' && !s.container.name.trim()) e.container = 'Enter a container name, or choose Clear.';
  if (s.customProperties.mode === 'REPLACE') {
    const p = parseKeyValues(s.customProperties.values, cpScalar);
    if (!p.ok) e.customProperties = p.message;
    else if (Object.keys(p.value).length === 0 && !s.customProperties.replaceAll) {
      e.customProperties = 'Enter at least one key=value line.';
    }
  }
  if (s.frontmatter.mode === 'REPLACE') {
    const p = parseKeyValues(s.frontmatter.values, fmScalar);
    if (!p.ok) e.frontmatter = p.message;
    else if (Object.keys(p.value).length === 0 && !s.frontmatter.replaceWhole) e.frontmatter = 'Enter at least one key=value line.';
  }
  if (s.workItemLinks.mode === 'REPLACE' && splitList(s.workItemLinks.items).length === 0) {
    e.workItemLinks = 'Enter at least one work item key (for example CTSK-123), or choose Clear.';
  }
  return e;
}

/** Pure: rows -> request mutations. NO_CHANGE rows and rows with errors are dropped. */
export function buildStage2Mutations(s: Stage2State): Stage2Mutations {
  const out: Stage2Mutations = {};
  const err = stage2Errors(s);

  if (!err.tags) {
    if (s.tags.mode === 'CLEAR') out.tags = { strategy: 'CLEAR' };
    else if (s.tags.mode === 'REPLACE') out.tags = { strategy: 'REPLACE', mode: s.tags.tagMode, names: splitList(s.tags.names) };
  }
  if (!err.container) {
    if (s.container.mode === 'CLEAR') out.container = { strategy: 'CLEAR' };
    else if (s.container.mode === 'REPLACE') out.container = { strategy: 'REPLACE', name: s.container.name.trim() };
  }
  if (!err.customProperties) {
    const cp = s.customProperties;
    if (cp.mode === 'CLEAR') {
      const names = splitList(cp.clearNames);
      out.customProperties = names.length ? { strategy: 'CLEAR', names } : { strategy: 'CLEAR' };
    } else if (cp.mode === 'REPLACE') {
      const p = parseKeyValues(cp.values, cpScalar);
      if (p.ok) {
        out.customProperties = {
          strategy: 'REPLACE',
          values: p.value as Record<string, string | number | boolean>,
          ...(cp.replaceAll ? { replaceAll: true } : {}),
        };
      }
    }
  }
  if (!err.frontmatter) {
    const fm = s.frontmatter;
    if (fm.mode === 'CLEAR') {
      const keys = splitList(fm.clearKeys);
      out.frontmatter = keys.length ? { strategy: 'CLEAR', keys } : { strategy: 'CLEAR' };
    } else if (fm.mode === 'REPLACE') {
      const p = parseKeyValues(fm.values, fmScalar);
      if (p.ok) out.frontmatter = { strategy: 'REPLACE', mode: fm.replaceWhole ? 'replace' : 'merge', values: p.value };
    }
  }
  if (!err.workItemLinks) {
    const wl = s.workItemLinks;
    if (wl.mode === 'CLEAR') {
      const items = splitList(wl.items);
      out.workItemLinks = items.length
        ? { strategy: 'CLEAR', context: wl.context, workItems: items }
        : { strategy: 'CLEAR', context: wl.context };
    } else if (wl.mode === 'REPLACE') {
      out.workItemLinks = { strategy: 'REPLACE', context: wl.context, workItems: splitList(wl.items) };
    }
  }
  return out;
}

/** Confirmation-line fragments for the changed Stage 2 rows. */
export function describeStage2(m: Stage2Mutations): string[] {
  const parts: string[] = [];
  if (m.tags) {
    parts.push(
      m.tags.strategy === 'CLEAR'
        ? 'remove all tags'
        : `${m.tags.mode === 'add' ? 'add' : m.tags.mode === 'remove' ? 'remove' : 'set'} tags ${(m.tags.names ?? []).join(', ')}`,
    );
  }
  if (m.container) parts.push(m.container.strategy === 'CLEAR' ? 'clear the container' : `move into container “${m.container.name}”`);
  if (m.customProperties) {
    const c = m.customProperties;
    parts.push(
      c.strategy === 'CLEAR'
        ? c.names?.length ? `clear custom properties ${c.names.join(', ')}` : 'clear ALL custom properties'
        : `set custom properties ${Object.keys(c.values ?? {}).join(', ')}${c.replaceAll ? ' (removing all others)' : ''}`,
    );
  }
  if (m.frontmatter) {
    const f = m.frontmatter;
    parts.push(
      f.strategy === 'CLEAR'
        ? f.keys?.length ? `clear frontmatter keys ${f.keys.join(', ')}` : 'clear ALL frontmatter'
        : `${f.mode === 'replace' ? 'replace all frontmatter with' : 'merge frontmatter keys'} ${Object.keys(f.values ?? {}).join(', ')}`,
    );
  }
  if (m.workItemLinks) {
    const w = m.workItemLinks;
    parts.push(
      w.strategy === 'CLEAR'
        ? `remove ${w.workItems?.length ? w.workItems.join(', ') : 'all'} “${w.context}” work-item links`
        : `set “${w.context}” work-item links to ${(w.workItems ?? []).join(', ')}`,
    );
  }
  return parts;
}

const MODES = [['NO_CHANGE', 'No change'], ['REPLACE', 'Set'], ['CLEAR', 'Clear']] as const;
const ERR_STYLE = { color: 'var(--danger-primary, #B3261E)', fontSize: 12, marginTop: 4 } as const;
const HINT_STYLE = { fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 } as const;

function RowShell({
  label, mode, onMode, error, children,
}: {
  label: string;
  mode: RowMode;
  onMode: (m: RowMode) => void;
  error?: string;
  children?: React.ReactNode;
}) {
  const name = useId();
  return (
    <fieldset style={{ border: 'none', margin: '0 0 var(--space-4) 0', padding: 0 }}>
      <legend style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{label}</legend>
      <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
        {MODES.map(([m, text]) => (
          <label key={m} style={{ display: 'inline-flex', gap: 4, fontSize: 13 }}>
            <input type="radio" name={name} checked={mode === m} onChange={() => onMode(m)} />
            {text}
          </label>
        ))}
      </div>
      {children}
      {error && <div role="alert" style={ERR_STYLE}>{error}</div>}
    </fieldset>
  );
}

export function BulkEditStage2Rows({ value, onChange }: { value: Stage2State; onChange: (s: Stage2State) => void }) {
  const errors = stage2Errors(value);
  const set = <K extends keyof Stage2State>(k: K, patch: Partial<Stage2State[K]>) =>
    onChange({ ...value, [k]: { ...value[k], ...patch } });
  const box = { width: '100%', marginTop: 6 } as const;

  return (
    <>
      <RowShell label="Tags" mode={value.tags.mode} onMode={(mode) => set('tags', { mode })} error={errors.tags}>
        {value.tags.mode === 'REPLACE' && (
          <div style={{ marginTop: 6 }}>
            <select className="input" aria-label="Tags mode" value={value.tags.tagMode}
              onChange={(e) => set('tags', { tagMode: e.target.value as Stage2State['tags']['tagMode'] })}>
              <option value="replace">Replace the whole tag set</option>
              <option value="add">Add to existing tags</option>
              <option value="remove">Remove from existing tags</option>
            </select>
            <input type="text" className="input" aria-label="Tags value" style={box} value={value.tags.names}
              placeholder="Tag names, comma separated" onChange={(e) => set('tags', { names: e.target.value })} />
          </div>
        )}
      </RowShell>

      <RowShell label="Container" mode={value.container.mode} onMode={(mode) => set('container', { mode })} error={errors.container}>
        {value.container.mode === 'REPLACE' && (
          <>
            <input type="text" className="input" aria-label="Container value" style={box} value={value.container.name}
              placeholder="Container name" onChange={(e) => set('container', { name: e.target.value })} />
            <div style={HINT_STYLE}>Moves each document into this container only (other memberships are removed).</div>
          </>
        )}
      </RowShell>

      <RowShell label="Custom properties" mode={value.customProperties.mode}
        onMode={(mode) => set('customProperties', { mode })} error={errors.customProperties}>
        {value.customProperties.mode === 'REPLACE' && (
          <>
            <textarea className="input" aria-label="Custom properties value" rows={3} style={box}
              value={value.customProperties.values} placeholder={'priority=high\nreviewed=true'}
              onChange={(e) => set('customProperties', { values: e.target.value })} />
            <label style={{ display: 'inline-flex', gap: 4, fontSize: 13, marginTop: 4 }}>
              <input type="checkbox" checked={value.customProperties.replaceAll}
                onChange={(e) => set('customProperties', { replaceAll: e.target.checked })} />
              Also remove every custom property not listed
            </label>
            <div style={HINT_STYLE}>One key=value per line. Other custom properties are left alone. Wrap a value in "quotes" to force text.</div>
          </>
        )}
        {value.customProperties.mode === 'CLEAR' && (
          <input type="text" className="input" aria-label="Custom properties to clear" style={box}
            value={value.customProperties.clearNames} placeholder="Keys to clear (blank = clear ALL custom properties)"
            onChange={(e) => set('customProperties', { clearNames: e.target.value })} />
        )}
      </RowShell>

      <RowShell label="Frontmatter" mode={value.frontmatter.mode}
        onMode={(mode) => set('frontmatter', { mode })} error={errors.frontmatter}>
        {value.frontmatter.mode === 'REPLACE' && (
          <>
            <textarea className="input" aria-label="Frontmatter value" rows={3} style={box}
              value={value.frontmatter.values} placeholder={'author=Ada\nreviewed=true'}
              onChange={(e) => set('frontmatter', { values: e.target.value })} />
            <label style={{ display: 'inline-flex', gap: 4, fontSize: 13, marginTop: 4 }}>
              <input type="checkbox" checked={value.frontmatter.replaceWhole}
                onChange={(e) => set('frontmatter', { replaceWhole: e.target.checked })} />
              Replace all frontmatter (otherwise only the listed keys change)
            </label>
            <div style={HINT_STYLE}>Top-level keys only. Values are read as JSON when they parse, otherwise as text.</div>
          </>
        )}
        {value.frontmatter.mode === 'CLEAR' && (
          <input type="text" className="input" aria-label="Frontmatter keys to clear" style={box}
            value={value.frontmatter.clearKeys} placeholder="Keys to clear (blank = clear ALL frontmatter)"
            onChange={(e) => set('frontmatter', { clearKeys: e.target.value })} />
        )}
      </RowShell>

      <RowShell label="Work-item links" mode={value.workItemLinks.mode}
        onMode={(mode) => set('workItemLinks', { mode })} error={errors.workItemLinks}>
        {value.workItemLinks.mode !== 'NO_CHANGE' && (
          <div style={{ marginTop: 6 }}>
            <select className="input" aria-label="Link context" value={value.workItemLinks.context}
              onChange={(e) => set('workItemLinks', { context: e.target.value as Stage2State['workItemLinks']['context'] })}>
              {LINK_CONTEXTS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="text" className="input" aria-label="Work item keys" style={box}
              value={value.workItemLinks.items}
              placeholder={value.workItemLinks.mode === 'CLEAR' ? 'Keys to unlink (blank = all links in this context)' : 'Work item keys, e.g. CTSK-123, CTSK-124'}
              onChange={(e) => set('workItemLinks', { items: e.target.value })} />
            <div style={HINT_STYLE}>Only links in the chosen context change; links in other contexts are never touched.</div>
          </div>
        )}
      </RowShell>
    </>
  );
}
